import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface MFAChallengeProps {
  onVerified: () => void;
  onLogout: () => void;
}

export const MFAChallenge: React.FC<MFAChallengeProps> = ({ onVerified, onLogout }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);

  useEffect(() => {
    loadFactor();
  }, []);

  const loadFactor = async () => {
    const { data, error: listError } = await supabase.auth.mfa.listFactors();
    if (listError || !data) return;
    const totp = data.totp?.[0];
    if (totp) setFactorId(totp.id);
  };

  const verify = async () => {
    if (!factorId || code.length !== 6) return;
    setLoading(true);
    setError(null);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });
      if (verifyError) throw verifyError;
      onVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && code.length === 6) verify();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--rp-bg)' }}>
      <div className="max-w-sm w-full rounded-2xl p-8 shadow-2xl" style={{ backgroundColor: 'var(--rp-surface, #1a1a2e)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold" style={{ color: 'var(--rp-text)' }}>Two-Factor Verification</h2>
          <p className="text-sm mt-2" style={{ color: 'var(--rp-text-secondary)' }}>
            Enter the 6-digit code from your authenticator app
          </p>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(255,59,48,0.15)', color: '#FF3B30', border: '1px solid rgba(255,59,48,0.3)' }}>
            {error}
          </div>
        )}

        <div className="space-y-3">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={handleKeyDown}
            placeholder="000000"
            autoFocus
            className="w-full px-4 py-3 rounded-lg text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--rp-text)', border: '1px solid rgba(255,255,255,0.15)' }}
          />
          <button
            onClick={verify}
            disabled={loading || code.length !== 6}
            className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
            style={{ backgroundColor: 'var(--rp-primary)', color: '#ffffff' }}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>

        <button onClick={onLogout} className="w-full mt-4 py-2 text-sm transition-all hover:opacity-70" style={{ color: 'var(--rp-text-tertiary)' }}>
          Sign out
        </button>
      </div>
    </div>
  );
};
